import { useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Stack,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { EmojiPicker, ColourPicker } from "../pickers";
import { ACTIVITY_COLORS } from "../../constants/activityColors";
import { ActivitiesContext } from "../../context/activities/ActivitiesContext";

const DURATION_OPTIONS = [
  { value: null, label: "No time limit" },
  { value: 15, label: "15 minutes" },
  { value: 30, label: "30 minutes" },
  { value: 60, label: "1 hour" },
  { value: 120, label: "2 hours" },
  { value: 180, label: "3 hours" },
  { value: 240, label: "4 hours" },
  { value: 300, label: "5 hours" },
  { value: 360, label: "6 hours" },
];

const ActivityDialog = () => {
  const {
    dialogOpen,
    editingActivity,
    closeDialog,
    activityName,
    setActivityName,
    activityEmoji,
    setActivityEmoji,
    activityDescription,
    setActivityDescription,
    activityColor,
    setActivityColor,
    activityDuration,
    setActivityDuration,
    saving,
    handleSave,
  } = useContext(ActivitiesContext);

  return (
    <Dialog
      open={dialogOpen}
      onClose={closeDialog}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle>
        {editingActivity ? "Edit Activity" : "Create Activity"}
      </DialogTitle>
      <DialogContent
        sx={{
          flex: 1,
          overflow: "auto",
          minHeight: 0,
        }}
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <EmojiPicker
              emoji={activityEmoji}
              setEmoji={setActivityEmoji}
              size={32}
            />
            <ColourPicker
              selectedColor={activityColor}
              onColorSelect={setActivityColor}
              colors={ACTIVITY_COLORS}
              size={32}
            />
            <TextField
              label="Activity Name"
              value={activityName}
              onChange={(e) => setActivityName(e.target.value)}
              fullWidth
              required
              inputProps={{ maxLength: 50 }}
              sx={{ mb: 0, "& .MuiInputBase-root": { m: 0 } }}
            />
          </Box>
          <TextField
            label="Description (optional)"
            value={activityDescription}
            onChange={(e) => setActivityDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
            inputProps={{ maxLength: 200 }}
          />
          <FormControl fullWidth size="small">
            <InputLabel id="duration-select-label">
              Duration (optional)
            </InputLabel>
            <Select
              labelId="duration-select-label"
              value={activityDuration ?? ""}
              label="Duration (optional)"
              onChange={(e) =>
                setActivityDuration(
                  e.target.value === "" ? null : e.target.value,
                )
              }
            >
              {DURATION_OPTIONS.map((option) => (
                <MenuItem
                  key={option.value ?? "none"}
                  value={option.value ?? ""}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          p: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Button onClick={closeDialog}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || !activityName.trim()}
          size="large"
          sx={{ minWidth: 100 }}
        >
          {saving ? <CircularProgress size={24} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ActivityDialog;
